import { AlertCircle, Inbox, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";
import clsx from "clsx";

export function money(paisa: unknown) {
  const amount = Number(paisa || 0) / 100;
  return new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR", maximumFractionDigits: 0 }).format(amount);
}

export function dateTime(value: unknown, dateOnly = false) {
  if (!value) return "-";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-PK", dateOnly ? { dateStyle: "medium", timeZone: "Asia/Karachi" } : { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Karachi" }).format(date);
}

export function titleCase(value: unknown) {
  return String(value || "-").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const statusTone: Record<string, string> = {
  delivered: "success", fulfilled: "success", paid: "success", reconciled: "success", complete: "success", packed: "success", active: "success", posted: "success",
  overdue: "danger", failed: "danger", critical: "danger", return_pending: "danger", return_in_transit: "danger", returned: "danger", quality_issue: "danger", amount_mismatch: "danger", rejected: "danger",
  attention: "warning", pending: "warning", pending_approval: "warning", partially_paid: "warning", due_today: "warning", payment_processing: "warning", delivered_unpaid: "warning", in_production: "warning", locked: "warning",
  dispatched: "info", picked_up: "info", in_transit: "info", arrived_at_destination: "info", out_for_delivery: "info", scheduled: "info",
};

const statusLabel: Record<string, string> = {
  paid: "Payment received",
  payment_processing: "Payment pending",
  cod_not_due: "Not due yet",
  amount_mismatch: "Amount mismatch",
};

export function StatusBadge({ value }: { value: unknown }) {
  const key = String(value || "unknown").toLowerCase();
  return <span className={clsx("status-badge", `status-${statusTone[key] || "neutral"}`)}>{statusLabel[key] || titleCase(key)}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header">
    <div><h1>{title}</h1>{description && <p>{description}</p>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function LoadingState({ label = "Loading live data" }: { label?: string }) {
  return <div className="state-panel"><LoaderCircle className="spin" size={22} /><span>{label}</span></div>;
}

export function EmptyState({ title = "Nothing here yet", detail }: { title?: string; detail?: string }) {
  return <div className="state-panel"><Inbox size={22} /><strong>{title}</strong>{detail && <span>{detail}</span>}</div>;
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div className="state-panel state-error"><AlertCircle size={22} /><strong>{message}</strong>{retry && <button className="button secondary" onClick={retry}>Try again</button>}</div>;
}

export function Metric({ label, value, note, tone }: { label: string; value: ReactNode; note?: string; tone?: string }) {
  return <div className={clsx("metric", tone && `metric-${tone}`)}><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</div>;
}
